import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import BookActionsMenu from './BookActionsMenu';

interface BookCardListProps {
  book: {
    book_id: number;
    title: string;
    author: string;
    cover_image_url?: string | null;
    price?: string;
    topic?: string;
    description?: string;
    average_rating?: number;
    rating?: number;
  };
  className?: string;
  progress?: number;
  showProgressBelow?: boolean;
  isSaved?: boolean;
  showActions?: boolean;
  onClick?: () => void;
  titleClassName?: string;
  authorClassName?: string;
}

const BookCardList: React.FC<BookCardListProps> = ({ book, className, progress, showProgressBelow, isSaved, showActions, onClick, titleClassName, authorClassName }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/library/book`, { state: { bookId: book.book_id } });
    }
  };

  return (
    <div 
      className={cn(
        'group flex items-center gap-4 py-3 cursor-pointer transition-colors duration-300 hover:bg-gray-50',
        className
      )}
      onClick={handleClick}
    >
      <div className="relative w-28 flex-shrink-0 aspect-[2/3] rounded-xl overflow-hidden shadow-[0_2px_7px_rgba(15,23,42,0.3)]">
        {book.cover_image_url ? (
          <img
            src={book.cover_image_url}
            alt={book.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
            <div className="text-3xl">📚</div>
          </div>
        )}

      </div>

      <div className="flex-1 flex items-center gap-2 py-1">
        <div className="flex-1 flex flex-col justify-center">
          <h3 className={cn('font-bold text-foreground text-sm leading-snug font-persian line-clamp-2 text-right', titleClassName)}>
            {book.title}
          </h3>
          <p className={cn('text-xs text-gray-500 font-persian', authorClassName)}>{book.author}</p>
          {showProgressBelow && progress != null && !Number.isNaN(Number(progress)) && Number(progress) >= 0 && (
            <div className="mt-2 w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-indigo-500"
                style={{ width: `${Math.max(0, Math.min(100, Number(progress)))}%` }}
              />
            </div>
          )}
        </div>
        {showActions && (
          <div className="ml-auto pl-2" onClick={(e) => e.stopPropagation()}>
            <BookActionsMenu 
              bookId={book.book_id} 
              title={book.title} 
              author={book.author} 
              isSaved={isSaved}
              triggerClassName="p-2 hover:bg-gray-100 rounded-lg"
              iconClassName="text-gray-600"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookCardList;
